import { useState } from "react";
import { firebase } from "common";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { IPlanType, IUserType } from "common/src/types";
import { useAlert } from "components/alert/AlertContext";
import { ThemedModalContainer } from "./ModalContainer";
import { ThemedInput } from "components/input/input/ThemedInput";
import { useTheme, Typography, Grid, Button, Box, CircularProgress, FormControl, InputLabel, Select, MenuItem, Paper, Chip, Divider, Stack, SelectChangeEvent } from "@mui/material";

interface ThemedDriverActivePlanProps {
    selectedDriver?: IUserType;
    activatePlanModalOpen: boolean;
    setActivatePlanModalOpen: (activatePlanModalOpen: boolean) => void;
}

export function ThemedDriverActivePlan({
    selectedDriver,
    activatePlanModalOpen,
    setActivatePlanModalOpen
}: ThemedDriverActivePlanProps) {
    const theme = useTheme();
    const { t } = useTranslation();
    const { showAlert } = useAlert();
    const [loading, setLoading] = useState(false);
    const plans = useSelector((state) => state.plans);
    const [selectedPlan, setSelectedPlan] = useState<string>("");
    const [activationPassword, setActivationPassword] = useState("");
    const settings = useSelector((state) => state.settingsdata.settings);

    const handlePlanChange = (event: SelectChangeEvent) => {
        setSelectedPlan(event.target.value);
    };

    const handleClose = () => {
        setSelectedPlan("");
        setActivationPassword("");
        setActivatePlanModalOpen(false);
    };

    const handleActivate = async () => {
        if (!selectedPlan) {
            showAlert({
                type: "WARNING",
                title: t('ALERT'),
                message: t("SELECT_A_PLAN")
            })
            return;
        }

        if (!activationPassword) {
            showAlert({
                type: "WARNING",
                title: t('ALERT'),
                message: t("password_required_plan")
            })
            return;
        }

        try {
            setLoading(true);

            const result = await firebase.callFunction('providersPagopar-manualActivatePlan', {
                planId: selectedPlan,
                password: activationPassword,
                driverId: selectedDriver?.id,
            });

            if (result.success) {
                showAlert({
                    type: "SUCCESS",
                    title: t('success'),
                    message: `✅ ${t("success")}`
                })
                handleClose();
            } else {
                showAlert({
                    type: "ERROR",
                    title: t('error'),
                    message: result.message || result.error || t("SUBIMIT_ERROR")
                })
            }
        } catch (e: any) {
            console.error(e);

            // Extrair mensagem de erro do Firebase
            let errorMessage = t("SUBIMIT_ERROR");

            if (e.message) {
                errorMessage = e.message;
            } else if (e.code) {
                // Firebase error codes
                switch (e.code) {
                    case 'permission-denied':
                        errorMessage = 'Você não tem permissão para esta ação.';
                        break;
                    case 'unauthenticated':
                        errorMessage = 'Usuário não autenticado.';
                        break;
                    default:
                        errorMessage = e.message || t("SUBIMIT_ERROR");
                }
            }

            showAlert({
                type: "ERROR",
                title: t('error'),
                message: errorMessage
            })
        } finally {
            setLoading(false);
        }
    };

    const activePlan = plans?.date?.find((plan: IPlanType) => plan.ref === selectedDriver?.currentPlan);

    return (
        <ThemedModalContainer
            open={activatePlanModalOpen}
            onClose={handleClose}
        >
            <Box sx={{ p: { xs: 1, sm: 2 } }}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Typography
                            variant="h5"
                            component="h2"
                            sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 1 }}
                        >
                            {t("ACTIVATE_PLAN_FOR")}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            <strong>
                                {selectedDriver?.firstName} {selectedDriver?.lastName}
                            </strong>
                        </Typography>
                        <Divider sx={{ mt: 2 }} />
                    </Grid>

                    {selectedDriver?.currentPlan && selectedDriver.paymentStatus === "active" ? (
                        <Grid item xs={12}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 4,
                                    borderRadius: 2,
                                    textAlign: "center",
                                    bgcolor: theme.palette.action.hover,
                                    border: `1px solid ${theme.palette.divider}`
                                }}
                            >
                                <Typography
                                    variant="h6"
                                    sx={{
                                        mb: 2,
                                        fontWeight: 600,
                                        color: theme.palette.info.main,
                                    }}
                                >
                                    {t("PLAN_ALREADY_ACTIVE")}
                                </Typography>

                                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                                    {t("THIS_DRIVER_ALREADY_HAS_ACTIVE_PLAN")}
                                </Typography>

                                {activePlan && (
                                    <Chip
                                        color="info"
                                        sx={{ mb: 3 }}
                                        variant="outlined"
                                        label={`📋 ${activePlan.title} - ${settings?.symbol} ${activePlan.price}`}
                                    />
                                )}

                                {!activePlan && (
                                    <Chip
                                        variant="outlined"
                                        sx={{ mb: 3, fontStyle: "italic" }}
                                        label={`ID: ${selectedDriver.currentPlan}`}
                                    />
                                )}

                                <Button
                                    sx={{ mt: 1 }}
                                    variant="contained"
                                    onClick={handleClose}
                                >
                                    {t("close")}
                                </Button>
                            </Paper>
                        </Grid>
                    ) : (
                        <>
                            <Grid item xs={12}>
                                <FormControl fullWidth required>
                                    <InputLabel id="plan-select-label">{t("SELECT_A_PLAN")}</InputLabel>
                                    <Select
                                        disabled={loading}
                                        value={selectedPlan}
                                        label={t("SELECT_A_PLAN")}
                                        onChange={handlePlanChange}
                                        labelId="plan-select-label"
                                    >
                                        <MenuItem value="" disabled>
                                            <em>{`-- ${t("SELECT_A_PLAN")} --`}</em>
                                        </MenuItem>
                                        {plans?.date?.map((plan: IPlanType) => (
                                            <MenuItem key={plan.ref} value={plan.ref}>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <span>{plan.title}</span>
                                                    <Chip
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                        label={`${settings?.symbol} ${plan.price}`}
                                                    />
                                                </Stack>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <ThemedInput
                                    required
                                    fullWidth
                                    type="password"
                                    disabled={loading}
                                    variant="outlined"
                                    label={t("password")}
                                    value={activationPassword}
                                    placeholder={t("enter_password_to_activate")}
                                    onChangeText={(text) => setActivationPassword(text)}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Stack direction="row" spacing={2} justifyContent="flex-end">
                                    <Button
                                        variant="outlined"
                                        onClick={handleClose}
                                        disabled={loading}
                                    >
                                        {t("cancel")}
                                    </Button>
                                    <Button
                                        variant="contained"
                                        onClick={handleActivate}
                                        disabled={loading || !activationPassword || !selectedPlan}
                                        startIcon={loading && <CircularProgress size={20} color="inherit" />}
                                    >
                                        {loading ? t("loading") : t("ACTIVE")}
                                    </Button>
                                </Stack>
                            </Grid>
                        </>
                    )}
                </Grid>
            </Box>
        </ThemedModalContainer>
    );
}